import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, MessageSquare } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";
const fmt = (n) => "£" + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function QueryPackPublic() {
  const { token } = useParams();
  const [pack, setPack] = useState(null);
  const [error, setError] = useState("");
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    axios.get(`${API}/frtx/public/query-pack/${token}`)
      .then((r) => {
        setPack(r.data);
        const seed = {};
        (r.data.questions || []).forEach((q) => { seed[q.index] = q.client_response || ""; });
        setAnswers(seed);
      })
      .catch((e) => setError(e.response?.data?.detail || e.message));
  }, [token]);

  async function submit() {
    setSaving(true);
    const responses = Object.entries(answers)
      .filter(([, v]) => (v || "").trim())
      .map(([index, response]) => ({ index: Number(index), response }));
    if (!responses.length) { setSaving(false); alert("Please answer at least one question."); return; }
    try {
      await axios.post(`${API}/frtx/public/query-pack/${token}/respond`, { responses });
      setSaved(true);
    } catch (e) {
      alert("Failed to submit: " + (e.response?.data?.detail || e.message));
    }
    setSaving(false);
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg)] p-6">
        <div className="card-flat p-8 max-w-md text-center">
          <div className="overline mb-2">Query pack</div>
          <h1 className="font-display text-2xl mb-2">Link not found</h1>
          <p className="text-sm text-[var(--ink-2)]">This query link is not valid, has expired, or has been withdrawn. Please contact your accountant.</p>
        </div>
      </div>
    );
  }
  if (!pack) return <div className="min-h-screen flex items-center justify-center text-[var(--ink-2)]">Loading…</div>;
  if (pack.status === "closed") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg)] p-6">
        <div className="card-flat p-8 max-w-md text-center">
          <CheckCircle2 size={40} strokeWidth={1.3} className="mx-auto mb-3 text-[var(--ok)]" />
          <h1 className="font-display text-2xl mb-2">Thanks — all done</h1>
          <p className="text-sm text-[var(--ink-2)]">Your responses have been received and applied. There's nothing further to do here.</p>
        </div>
      </div>
    );
  }
  if (saved) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg)] p-6">
        <div className="card-flat p-8 max-w-md text-center">
          <CheckCircle2 size={40} strokeWidth={1.3} className="mx-auto mb-3 text-[var(--ok)]" />
          <h1 className="font-display text-2xl mb-2">Responses received</h1>
          <p className="text-sm text-[var(--ink-2)]">Thanks — your accountant will pick these up shortly.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg)] py-10 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="mb-6 flex items-center gap-3">
          <MessageSquare size={22} strokeWidth={1.4} className="text-[var(--gold)]" />
          <div>
            <div className="overline">Finchley Business Services</div>
            <h1 className="font-display text-3xl">Quick questions on your {pack.year} bank transactions</h1>
          </div>
        </div>
        <p className="text-sm text-[var(--ink-2)] mb-6 leading-relaxed">
          Hi {pack.client_name} — we're preparing your {pack.year} accounts and have a few queries on individual transactions.
          Please leave a short note next to each one so we can categorise them correctly. You can save and come back later.
        </p>

        <div className="space-y-4 mb-8">
          {pack.questions.map((q) => (
            <div key={q.index} className="card-flat p-4" data-testid={`qp-pub-q-${q.index}`}>
              <div className="flex justify-between items-start mb-2 gap-3">
                <div className="text-xs font-mono-data text-[var(--ink-2)]">
                  <b className="text-[var(--ink)]">{q.date}</b> · {q.is_debit ? "OUT" : "IN"} · <b>{fmt(q.amount)}</b> · {q.description}
                </div>
              </div>
              <p className="text-sm mb-2">{q.question_text}</p>
              <textarea
                data-testid={`qp-pub-answer-${q.index}`}
                value={answers[q.index] || ""}
                onChange={(e) => setAnswers({ ...answers, [q.index]: e.target.value })}
                placeholder="Your answer…"
                className="input h-20 text-sm"
              />
            </div>
          ))}
        </div>

        <div className="flex justify-end">
          <button data-testid="qp-pub-submit" disabled={saving} onClick={submit} className="btn-primary text-sm !py-2 !px-6">
            {saving ? "Submitting…" : "Submit responses"}
          </button>
        </div>

        <footer className="mt-10 pt-4 border-t border-[var(--line)] text-[11px] text-[var(--ink-2)] text-center">
          © {new Date().getFullYear()} Finchley Business Services · <a href="https://finchleybusiness.co.uk" className="underline">finchleybusiness.co.uk</a>
        </footer>
      </div>
    </div>
  );
}
